import { useTranslation } from "react-i18next"

import type { GroupedCitations } from "../../types/active-citations"

import { badgeVariants } from "@/components/ui/badge-variants"
import { Label } from "@/components/ui/label"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { useCitationLibrary } from "../../hooks/use-citation-library"

type LibraryCheckboxProps = {
  library: string
  citationsGroupedByLibrary: GroupedCitations
  sessionType: string
}

export function LibraryCheckbox({
  library,
  citationsGroupedByLibrary,
  sessionType,
}: LibraryCheckboxProps) {
  const { t } = useTranslation("", { keyPrefix: "settingsmenu" })
  const { getLibraryCounts, toggleAllActiveCitationsInLibrary } = useCitationLibrary(
    citationsGroupedByLibrary,
    sessionType
  )
  const libraryCounts = getLibraryCounts(library)
  const countLabel = t("active_citations_count", {
    active: libraryCounts.active,
    total: libraryCounts.total,
  })
  const canToggle = libraryCounts.total > 0
  const isAllActive = libraryCounts.total > 0 && libraryCounts.active === libraryCounts.total
  const actionLabel = isAllActive ? t("active_citations_clear") : t("active_citations_select_all")
  const badgeClassName = cn(
    badgeVariants({ variant: "outline" }),
    "h-6 w-[52px] shrink-0 justify-center overflow-hidden px-2 text-[11px] font-semibold leading-none transition-[width,margin-left,color,background-color,border-color]",
    libraryCounts.active > 0
      ? "border-primary/30 bg-primary/10 text-primary"
      : "border-border bg-muted/40 text-muted-foreground",
    canToggle &&
      "group-hover/library:w-[96px] group-hover/library:-ml-[44px] group-hover/library:border-primary/40 group-hover/library:bg-primary/15 group-hover/library:text-primary"
  )

  return (
    <div className="group/library flex min-w-0 flex-1 items-start justify-between gap-2">
      <div
        className={cn(
          "min-w-0 flex-1 pr-0 transition-[padding]",
          canToggle && "group-hover/library:pr-[44px]"
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Label className="line-clamp-2 block text-sm leading-snug font-medium">{library}</Label>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{library}</p>
          </TooltipContent>
        </Tooltip>
        <span className="text-muted-foreground text-xs">{countLabel}</span>
      </div>
      <div className="flex shrink-0 items-center">
        {libraryCounts.total > 0 ? (
          <button
            aria-label={actionLabel}
            aria-pressed={isAllActive}
            className={cn(badgeClassName, "relative z-10 cursor-pointer border-0")}
            title={actionLabel}
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              toggleAllActiveCitationsInLibrary(library, !isAllActive)
            }}
          >
            <span className="relative flex h-full w-full items-center justify-center">
              <span className="absolute inset-0 flex items-center justify-center transition-opacity group-hover/library:opacity-0">
                <span className="tabular-nums">
                  {libraryCounts.active}/{libraryCounts.total}
                </span>
              </span>
              <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover/library:opacity-100">
                <span className="truncate px-1 text-[10px]">{actionLabel}</span>
              </span>
            </span>
          </button>
        ) : (
          <span className={badgeClassName} title={countLabel}>
            {libraryCounts.active}/{libraryCounts.total}
          </span>
        )}
      </div>
    </div>
  )
}
